import React, { useMemo } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, shape, string, bool } from 'prop-types'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import BrowseAllRow from './Components/BrowseAllRow/BrowseAllRow'
import Categories from './Components/Categories/Categories'
import FilterColumn from './Components/FilterColumn/FilterColumn'
import MegaFooter from './Components/MegaFooter/MegaFooter'

// Shared category mega-menu panel for the redesigned navigation. Composes the
// categories grid, optional Type column, Budget column and the footer actions from
// the shared row primitive so the 3D Models panel (now) and the 3D Print panel
// (later) render identically. `types` is optional - the Print panel omits it.
//
// Layout is a single markup that reflows via CSS: three side-by-side columns plus a
// horizontal footer at md+, and a stacked accordion (browse-all row, categories,
// shaded Type/Budget pair, stacked actions) below md.
function CategoryMegaMenu({
  categories,
  types = EMPTY_ARRAY,
  budgets = EMPTY_ARRAY,
  baseUrl,
  browseAllHref,
  translations,
}) {
  // 3D Printing has its own top-level menu, so the "printable" type never belongs in the
  // 3D Models Type column - drop it here as the legacy TypeList did. (On the Print panel
  // `types` is empty, so this is a no-op there.)
  const typeItems = useMemo(() => (
    types
      .filter((type) => type.icon !== 'printable')
      .map((type) => ({ label: type.name, iconSlug: type.icon, href: type.path }))
  ), [types])

  const budgetItems = useMemo(() => (
    budgets.map((budget) => ({ label: budget.title, iconSlug: budget.icon, href: budget.path }))
  ), [budgets])

  // The 3D Printing panel omits Type, leaving only categories + budget. The `--no-type`
  // modifier narrows the panel to that two-column width (648px vs the 872px 3-column one).
  const hasTypeColumn = typeItems.length > 0

  return (
    <div className={classnames('category-mega-menu', !hasTypeColumn && 'category-mega-menu--no-type')}>
      <BrowseAllRow href={browseAllHref} label={translations.browseAll} />

      <div className="category-mega-menu__body">
        <Categories
          categories={categories}
          title={translations.categoriesTitle}
          browseAllHref={browseAllHref}
          browseAllLabel={translations.browseAll}
        />

        <div className="category-mega-menu__filters">
          {hasTypeColumn && (
            <FilterColumn items={typeItems} title={translations.typesTitle} variant="type" />
          )}
          <FilterColumn items={budgetItems} title={translations.budgetTitle} variant="budget" />
        </div>
      </div>

      <MegaFooter baseUrl={baseUrl} translations={translations.actions} />
    </div>
  )
}

CategoryMegaMenu.propTypes = {
  categories: arrayOf(shape({
    title: string.isRequired,
    slug: string,
    path: string,
  })).isRequired,
  types: arrayOf(shape({
    icon: string,
    name: string,
    path: string,
  })),
  budgets: arrayOf(shape({
    title: string,
    path: string,
    icon: string,
    withSub: bool,
  })),
  baseUrl: string.isRequired,
  browseAllHref: string.isRequired,
  translations: shape({
    categoriesTitle: string.isRequired,
    typesTitle: string,
    budgetTitle: string.isRequired,
    browseAll: string.isRequired,
    actions: shape({
      new: string.isRequired,
      trending: string.isRequired,
      topSelling: string.isRequired,
      allModels: string.isRequired,
    }).isRequired,
  }).isRequired,
}

export default CategoryMegaMenu
